import { lazy, Suspense, useState } from 'react';
import type { Geometry } from 'geojson';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { DIALOG_SURFACE } from './AssignmentList/assignmentCardStyles';
import { ICS215A_RISK_GAIN_OPTIONS } from './ics215aBoxLabels';

// The picker pulls in the ArcGIS map, which is far too heavy to load with a
// dialog most people open only to retype a hazard.
const IncidentGeoLocationPicker = lazy(() =>
  import('components/IncidentConfig/IncidentGeoLocationPicker').then(
    (module) => ({ default: module.IncidentGeoLocationPicker })
  )
);

/**
 * Base UI's Select has no empty-string value, so the cleared state needs a
 * token. Its trigger prints the raw value rather than the item's label, so the
 * token has to read as the words it stands for. It never reaches the database:
 * saving turns it back into null, which the export prints as the sheet's own
 * blank Risk/Gain option.
 */
const NO_RISK_GAIN = 'Not Applicable';

export type IncidentAreaDraft = {
  areaName: string;
  hazard: string;
  mitigations: string;
  riskGain: string | null;
  geometry?: Geometry | null;
};

export const BLANK_INCIDENT_AREA: IncidentAreaDraft = {
  areaName: '',
  hazard: '',
  mitigations: '',
  riskGain: null,
  geometry: null,
};

export type IncidentAreaEditDialogProps = {
  /** True when adding a row; the header and save button word themselves from it. */
  isNewArea: boolean;
  area: IncidentAreaDraft;
  onDismiss: () => void;
  onSave: (area: IncidentAreaDraft) => void;
};

/**
 * Boxes 5 to 8 of the 215A for one area, which the table edits a whole row at a
 * time rather than cell by cell.
 *
 * Risk/Gain is optional because the printed form's dropdown may legitimately be
 * left blank; the other three are what make the row worth printing.
 */
const IncidentAreaEditDialog: React.FC<IncidentAreaEditDialogProps> = ({
  isNewArea,
  area,
  onDismiss,
  onSave,
}) => {
  const [draft, setDraft] = useState<IncidentAreaDraft>(area);
  const [showPicker, setShowPicker] = useState(false);

  const set = <K extends keyof IncidentAreaDraft>(
    key: K,
    value: IncidentAreaDraft[K]
  ) => setDraft((current) => ({ ...current, [key]: value }));

  const saveDisabled =
    !draft.areaName.trim() || !draft.hazard.trim() || !draft.mitigations.trim();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
    >
      <DialogContent
        className={`${DIALOG_SURFACE} tw:sm:max-w-2xl`}
        showCloseButton={false}
        data-testid="ics215a-area-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isNewArea ? 'Add Incident Area' : 'Edit Incident Area'}
          </DialogTitle>
        </DialogHeader>

        {/* The geolocation picker opens a 400px map, which needs somewhere to go. */}
        <div className="tw:grid tw:max-h-[60vh] tw:gap-4 tw:overflow-y-auto">
          <Field>
            <FieldLabel htmlFor="incident-area-name">Area Name</FieldLabel>
            <Input
              id="incident-area-name"
              value={draft.areaName}
              onChange={(event) => set('areaName', event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="incident-area-hazard">
              Hazards / Risks
            </FieldLabel>
            <Input
              id="incident-area-hazard"
              value={draft.hazard}
              onChange={(event) => set('hazard', event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="incident-area-mitigations">
              Mitigations
            </FieldLabel>
            <Input
              id="incident-area-mitigations"
              value={draft.mitigations}
              onChange={(event) => set('mitigations', event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="incident-area-risk-gain">Risk/Gain</FieldLabel>
            <Select
              value={draft.riskGain ?? NO_RISK_GAIN}
              onValueChange={(value) =>
                set('riskGain', value === NO_RISK_GAIN ? null : String(value))
              }
            >
              <SelectTrigger id="incident-area-risk-gain">
                <SelectValue placeholder="Select Risk/Gain..." />
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value={NO_RISK_GAIN}>{NO_RISK_GAIN}</SelectItem>
                {ICS215A_RISK_GAIN_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Geolocation</FieldLabel>
            <FieldDescription>
              Optional. Drop a point or draw the area to place it on the 215A
              Incident Areas map layer.
            </FieldDescription>
            {/* Behind a button, so the ArcGIS chunk is fetched only by someone
                who means to place the area, not by everyone retyping a hazard. */}
            {showPicker ? (
              <Suspense
                fallback={
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    Loading the map…
                  </p>
                }
              >
                <IncidentGeoLocationPicker
                  geometry={draft.geometry ?? undefined}
                  onGeometryChange={(next) => set('geometry', next)}
                  onAddressChange={() => {}}
                />
              </Suspense>
            ) : (
              <div className="tw:flex tw:items-center tw:gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPicker(true)}
                >
                  {draft.geometry ? 'View / edit location' : 'Set location'}
                </Button>
                <span className="tw:text-sm tw:text-muted-foreground">
                  {draft.geometry
                    ? `${draft.geometry.type === 'Point' ? 'Point' : 'Area'} set`
                    : 'No location set'}
                </span>
              </div>
            )}
          </Field>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDismiss}>
            Close
          </Button>
          <Button
            type="button"
            disabled={saveDisabled}
            onClick={() => onSave(draft)}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IncidentAreaEditDialog;
